/**
 * Teen Onboarding Flow
 * A beautiful, immersive onboarding experience that adapts to teen preferences
 * Step-by-step: Age → Gender (optional) → Theme Preview → Confirmation
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, 
  ChevronRight, 
  Check, 
  User, 
  Palette, 
  ArrowRight,
  Moon,
  Sun,
  Stars
} from 'lucide-react';
import { useTeenTheme, AGE_OPTIONS, GENDER_OPTIONS, TEEN_THEMES, CUSTOM_PALETTES } from '../contexts/TeenThemeContext';

// Animated background orbs
const BackgroundOrbs = ({ theme }) => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    <motion.div 
      className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-30"
      style={{ 
        background: `radial-gradient(circle, ${theme?.colors?.primary || '#818CF8'} 0%, transparent 70%)` 
      }}
      animate={{ 
        x: [0, 30, 0],
        y: [0, -20, 0],
      }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div 
      className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full opacity-20"
      style={{ 
        background: `radial-gradient(circle, ${theme?.colors?.accent || '#F472B6'} 0%, transparent 70%)` 
      }}
      animate={{ 
        x: [0, -20, 0],
        y: [0, 30, 0],
      }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div 
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-10"
      style={{ 
        background: `radial-gradient(circle, ${theme?.colors?.glow || 'rgba(129, 140, 248, 0.3)'} 0%, transparent 60%)` 
      }}
      animate={{ scale: [1, 1.1, 1] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

// Age selection card
const AgeCard = ({ option, isSelected, onSelect, theme }) => {
  const icons = {
    younger: Sun,
    mid: Stars,
    older: Moon,
  };
  const Icon = icons[option.id];
  
  return (
    <motion.button
      onClick={() => onSelect(option.id)}
      className={`relative w-full p-6 rounded-2xl text-left transition-all duration-300 overflow-hidden group ${
        isSelected 
          ? 'ring-2 ring-offset-2 ring-offset-transparent'
          : 'hover:scale-[1.02]'
      }`}
      style={{
        background: isSelected 
          ? `linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.accent}20)`
          : theme.mode === 'dark' 
            ? 'rgba(255,255,255,0.05)' 
            : 'rgba(0,0,0,0.02)',
        borderColor: isSelected ? theme.colors.primary : 'transparent',
        ringColor: isSelected ? theme.colors.primary : 'transparent',
      }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      data-testid={`age-option-${option.id}`}
    >
      {/* Glow effect on hover */}
      <motion.div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(circle at center, ${theme.colors.glow}, transparent 70%)`,
        }}
      />
      
      <div className="relative flex items-center gap-4">
        <div 
          className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all ${
            isSelected ? 'scale-110' : ''
          }`}
          style={{
            background: isSelected 
              ? `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`
              : theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          }}
        >
          <Icon 
            size={24} 
            style={{ color: isSelected ? '#fff' : theme.colors.textSecondary }}
          />
        </div>
        
        <div className="flex-1">
          <p 
            className="text-2xl font-semibold font-outfit tracking-tight"
            style={{ color: theme.colors.textPrimary }}
          >
            {option.label}
          </p>
          <p 
            className="text-sm mt-0.5"
            style={{ color: theme.colors.textSecondary }}
          >
            {option.description}
          </p>
        </div>
        
        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: theme.colors.primary }}
          >
            <Check size={16} className="text-white" />
          </motion.div>
        )}
      </div>
    </motion.button>
  );
};

// Gender selection card (smaller, more subtle)
const GenderCard = ({ option, isSelected, onSelect, theme }) => (
  <motion.button
    onClick={() => onSelect(option.id)}
    className={`p-4 rounded-xl text-center transition-all ${
      isSelected ? 'ring-2' : ''
    }`}
    style={{
      background: isSelected 
        ? `${theme.colors.primary}15`
        : theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
      ringColor: isSelected ? theme.colors.primary : 'transparent',
    }}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    data-testid={`gender-option-${option.id}`}
  >
    <p 
      className="font-medium"
      style={{ color: isSelected ? theme.colors.primary : theme.colors.textPrimary }}
    >
      {option.label}
    </p>
  </motion.button>
);

// Theme preview card
const ThemePreviewCard = ({ themeConfig, palette, isActive, onClick }) => (
  <motion.button
    onClick={onClick}
    className={`relative p-4 rounded-xl overflow-hidden transition-all ${
      isActive ? 'ring-2 ring-white/50' : ''
    }`}
    style={{ background: palette.bg }}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    data-testid={`theme-preview-${palette.id}`}
  >
    <div className="relative z-10">
      <div className="flex gap-2 mb-3">
        <div 
          className="w-6 h-6 rounded-full"
          style={{ background: palette.primary }}
        />
        <div 
          className="w-6 h-6 rounded-full"
          style={{ background: palette.accent }}
        />
      </div>
      <p 
        className="text-sm font-medium"
        style={{ color: themeConfig.mode === 'dark' ? '#fff' : '#1E293B' }}
      >
        {palette.name}
      </p>
    </div>
    
    {/* Decorative gradient */}
    <div 
      className="absolute bottom-0 right-0 w-20 h-20 opacity-30"
      style={{
        background: `radial-gradient(circle, ${palette.accent} 0%, transparent 70%)`,
      }}
    />
    
    {isActive && (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white flex items-center justify-center"
      >
        <Check size={14} style={{ color: palette.primary }} />
      </motion.div>
    )}
  </motion.button>
);

// Main Onboarding Component
const TeenOnboardingFlow = ({ onComplete }) => {
  const { completeOnboarding } = useTeenTheme();
  const [step, setStep] = useState(1); // 1: Age, 2: Gender, 3: Theme, 4: Confirm
  const [selectedAge, setSelectedAge] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedPalette, setSelectedPalette] = useState(null);
  
  // Get theme based on selected age
  const currentTheme = selectedAge ? TEEN_THEMES[selectedAge] : TEEN_THEMES.mid;
  const availablePalettes = selectedAge ? CUSTOM_PALETTES[selectedAge] : CUSTOM_PALETTES.mid;

  // Auto-select first palette when age changes
  useEffect(() => {
    if (selectedAge && availablePalettes) {
      setSelectedPalette(availablePalettes[0]);
    }
  }, [selectedAge, availablePalettes]);

  const handleComplete = () => {
    completeOnboarding({
      ageGroup: selectedAge,
      gender: selectedGender,
      customPalette: selectedPalette,
    });
    onComplete?.();
  };

  const canProceed = {
    1: !!selectedAge,
    2: true, // Gender is optional
    3: !!selectedPalette,
    4: true, // Final step - always allow completion
  };

  const stepTitles = {
    1: { title: "How old are you?", subtitle: "This helps us personalize your experience" },
    2: { title: "How do you identify?", subtitle: "Optional — skip if you prefer" },
    3: { title: "Choose your vibe", subtitle: "You can change this anytime" },
    4: { title: "You're all set!", subtitle: "Your space is ready" },
  };

  return (
    <motion.div 
      className="fixed inset-0 z-50 overflow-hidden"
      style={{ background: currentTheme.colors.base }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Animated background */}
      <BackgroundOrbs theme={currentTheme} />
      
      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Progress bar */}
        <div className="p-6">
          <div className="flex gap-2 max-w-md mx-auto">
            {[1, 2, 3, 4].map((s) => (
              <motion.div
                key={s}
                className="flex-1 h-1 rounded-full overflow-hidden"
                style={{ background: `${currentTheme.colors.textMuted}30` }}
              >
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: currentTheme.colors.primary }}
                  initial={{ width: 0 }}
                  animate={{ width: step >= s ? '100%' : '0%' }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 overflow-y-auto px-6 pb-32">
          <div className="max-w-md mx-auto">
            <AnimatePresence mode="wait">
              {/* Step 1: Age Selection */}
              {step === 1 && (
                <motion.div
                  key="age"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <div className="text-center pt-8">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.2 }}
                      className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                      style={{ 
                        background: `linear-gradient(135deg, ${currentTheme.colors.primary}30, ${currentTheme.colors.accent}30)` 
                      }}
                    >
                      <Sparkles size={28} style={{ color: currentTheme.colors.primary }} />
                    </motion.div>
                    <h1 
                      className="text-3xl font-semibold font-outfit tracking-tight mb-2"
                      style={{ color: currentTheme.colors.textPrimary }}
                    >
                      {stepTitles[1].title}
                    </h1>
                    <p style={{ color: currentTheme.colors.textSecondary }}>
                      {stepTitles[1].subtitle}
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {AGE_OPTIONS.map((option) => (
                      <AgeCard
                        key={option.id}
                        option={option}
                        isSelected={selectedAge === option.id}
                        onSelect={setSelectedAge}
                        theme={currentTheme}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 2: Gender Selection */}
              {step === 2 && (
                <motion.div
                  key="gender"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <div className="text-center pt-8">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.2 }}
                      className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                      style={{ 
                        background: `linear-gradient(135deg, ${currentTheme.colors.primary}30, ${currentTheme.colors.accent}30)` 
                      }}
                    >
                      <User size={28} style={{ color: currentTheme.colors.primary }} />
                    </motion.div>
                    <h1 
                      className="text-3xl font-semibold font-outfit tracking-tight mb-2"
                      style={{ color: currentTheme.colors.textPrimary }}
                    >
                      {stepTitles[2].title}
                    </h1>
                    <p style={{ color: currentTheme.colors.textSecondary }}>
                      {stepTitles[2].subtitle}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {GENDER_OPTIONS.map((option) => (
                      <GenderCard
                        key={option.id}
                        option={option}
                        isSelected={selectedGender === option.id}
                        onSelect={setSelectedGender}
                        theme={currentTheme}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 3: Theme Selection */}
              {step === 3 && (
                <motion.div
                  key="theme"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-8"
                >
                  <div className="text-center pt-8">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.2 }}
                      className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                      style={{ 
                        background: `linear-gradient(135deg, ${currentTheme.colors.primary}30, ${currentTheme.colors.accent}30)` 
                      }}
                    >
                      <Palette size={28} style={{ color: currentTheme.colors.primary }} />
                    </motion.div>
                    <h1 
                      className="text-3xl font-semibold font-outfit tracking-tight mb-2"
                      style={{ color: currentTheme.colors.textPrimary }}
                    >
                      {stepTitles[3].title}
                    </h1>
                    <p style={{ color: currentTheme.colors.textSecondary }}>
                      {stepTitles[3].subtitle}
                    </p>
                  </div>
                  
                  {/* Theme name badge */}
                  <div className="text-center">
                    <span 
                      className="px-4 py-1.5 rounded-full text-sm font-medium"
                      style={{ 
                        background: `${currentTheme.colors.primary}20`,
                        color: currentTheme.colors.primary,
                      }}
                    >
                      {currentTheme.name}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {availablePalettes?.map((palette) => (
                      <ThemePreviewCard
                        key={palette.id}
                        themeConfig={currentTheme}
                        palette={palette}
                        isActive={selectedPalette?.id === palette.id}
                        onClick={() => setSelectedPalette(palette)}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 4: Confirmation */}
              {step === 4 && (
                <motion.div
                  key="confirm"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="text-center pt-16 space-y-8"
                >
                  {/* Success animation */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.3 }}
                    className="w-24 h-24 mx-auto rounded-full flex items-center justify-center"
                    style={{ 
                      background: `linear-gradient(135deg, ${selectedPalette?.primary || currentTheme.colors.primary}, ${selectedPalette?.accent || currentTheme.colors.accent})` 
                    }}
                  >
                    <Check size={40} className="text-white" />
                  </motion.div>
                  
                  <div>
                    <h1 
                      className="text-3xl font-semibold font-outfit tracking-tight mb-2"
                      style={{ color: currentTheme.colors.textPrimary }}
                    >
                      {stepTitles[4].title}
                    </h1>
                    <p style={{ color: currentTheme.colors.textSecondary }}>
                      {stepTitles[4].subtitle}
                    </p>
                  </div>
                  
                  {/* Preview card */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="p-6 rounded-2xl"
                    style={{ 
                      background: currentTheme.colors.surface,
                      border: `1px solid ${currentTheme.colors.border}`,
                    }}
                  >
                    <p 
                      className="text-sm uppercase tracking-wider mb-1"
                      style={{ color: currentTheme.colors.textMuted }}
                    >
                      Your Theme
                    </p>
                    <p 
                      className="text-xl font-semibold font-outfit"
                      style={{ color: currentTheme.colors.textPrimary }}
                    >
                      {currentTheme.name}
                    </p>
                    <div className="flex justify-center gap-2 mt-3">
                      <div 
                        className="w-8 h-8 rounded-full"
                        style={{ background: selectedPalette?.primary || currentTheme.colors.primary }}
                      />
                      <div 
                        className="w-8 h-8 rounded-full"
                        style={{ background: selectedPalette?.accent || currentTheme.colors.accent }}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom navigation */}
        <div 
          className="fixed bottom-0 left-0 right-0 p-6"
          style={{ 
            background: `linear-gradient(to top, ${currentTheme.colors.base}, transparent)`,
          }}
        >
          <div className="max-w-md mx-auto flex gap-3">
            {step > 1 && step < 4 && (
              <motion.button
                onClick={() => setStep(step - 1)}
                className="px-6 py-4 rounded-xl font-medium"
                style={{ 
                  background: `${currentTheme.colors.textMuted}20`,
                  color: currentTheme.colors.textSecondary,
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Back
              </motion.button>
            )}
            
            <motion.button
              onClick={() => {
                if (step === 4) {
                  handleComplete();
                } else {
                  setStep(step + 1);
                }
              }}
              disabled={!canProceed[step]}
              className={`flex-1 px-6 py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-opacity ${
                !canProceed[step] ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{ 
                background: `linear-gradient(135deg, ${selectedPalette?.primary || currentTheme.colors.primary}, ${selectedPalette?.accent || currentTheme.colors.accent})`,
                color: '#fff',
              }}
              whileHover={canProceed[step] ? { scale: 1.02 } : {}}
              whileTap={canProceed[step] ? { scale: 0.98 } : {}}
              data-testid="onboarding-continue-btn"
            >
              {step === 4 ? 'Enter Reclaim' : step === 2 ? 'Continue' : 'Next'}
              <ArrowRight size={18} />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TeenOnboardingFlow;
